import { useId, useState } from 'react';
import { previewObjectivesAction, type AnalyticsEvent } from '../../core/events';
import { EXAMPLE } from '../../core/example';

export function focusOwnPlan(target: HTMLElement | null) {
  if (!target) return;
  const control = target.querySelector<HTMLElement>('select:not(:disabled), input:not(:disabled), button:not(:disabled)');
  (control ?? target).focus({ preventScroll: true });
  target.scrollIntoView({ block: 'start' });
}

function FractionIllustration() {
  return (
    <div className="fraction-illustration" role="img" aria-label="Fraction strips with the same-size whole: one half is larger than one quarter.">
      <div className="fraction-row" aria-hidden="true"><span>½</span><div className="fraction-strip halves"><i /><i /></div></div>
      <div className="fraction-row" aria-hidden="true"><span>¼</span><div className="fraction-strip quarters"><i /><i /><i /><i /></div></div>
    </div>
  );
}

function ExampleMatch({ onSourceClick, showEvidence = false }: { onSourceClick: () => void; showEvidence?: boolean }) {
  const verdict = { covered: 'Covered', partial: 'Related match', weak: 'Weak match' }[EXAMPLE.finding.band];
  return (
    <div className="example-match" data-band={EXAMPLE.finding.band}>
      <div className="example-standard-head">
        <span className="code">{EXAMPLE.label}</span>
        <span className="example-verdict">{verdict}</span>
      </div>
      <p className="example-standard">{EXAMPLE.standard.description}</p>
      {showEvidence && <blockquote><span className="evidence-label">Words from the sample plan</span><br />{EXAMPLE.lesson}</blockquote>}
      <p className="provenance">{EXAMPLE.standard.author} · {EXAMPLE.standard.statementType}<br />
        <a href={EXAMPLE.standard.caseIdentifierURI} target="_blank" rel="noopener noreferrer" onClick={onSourceClick}>Read {EXAMPLE.label} at Texas Gateway <span aria-hidden="true">↗</span></a>
      </p>
    </div>
  );
}

export default function ObjectivesWelcome({ surface, onTrack, onUseOwnPlan }: {
  surface: 'signed_in' | 'signed_out';
  onTrack: (event: AnalyticsEvent) => void;
  onUseOwnPlan: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const exampleId = useId();
  const titleId = useId();
  const descriptionId = useId();
  const [view, setView] = useState<'plan' | 'standards'>('plan');
  const track = (action: 'example_opened' | 'example_closed' | 'example_view_changed' | 'own_plan_clicked', properties: AnalyticsEvent['properties'] = {}) => {
    onTrack(previewObjectivesAction(action, { surface, sample: true, ...properties }));
  };
  const trackSource = () => onTrack(previewObjectivesAction('source_link_clicked', { surface, sample: true, statement_code: EXAMPLE.standard.statementCode, source: 'example' }));
  const goToOwnPlan = () => {
    setExpanded(false);
    track('example_closed', { reason: 'own_plan' });
    track('own_plan_clicked', { from: 'example' });
    onUseOwnPlan();
  };

  return (
    <>
      <div className="intro welcome-intro">
        <p className="eyebrow">Material you found</p>
        <h1>You found the material.<br /><span>Check it against the standards.</span></h1>
        <p className="tagline">Upload a lesson or worksheet and see which standards it may match, with the words from the material that support each one.</p>
      </div>
      <section className="example-disclosure" aria-label="Explore an example">
        <div className="example-disclosure-row">
          <p>Want a quick look first?</p>
          <button type="button" className="link example-disclosure-toggle" aria-expanded={expanded} aria-controls={exampleId} onClick={() => {
            if (expanded) {
              setExpanded(false);
              track('example_closed', { reason: 'close' });
            } else {
              setView('plan');
              setExpanded(true);
              track('example_opened');
            }
          }}>{expanded ? 'Hide example' : 'See an example'} <span aria-hidden="true">{expanded ? '−' : '+'}</span></button>
        </div>
        <div id={exampleId} hidden={!expanded}>
          {expanded && <section className="example-preview" aria-labelledby={titleId} aria-describedby={descriptionId}>
            <p className="eyebrow">A sample report · {surface === 'signed_out' ? 'No sign-in needed' : 'No analysis attempt used'}</p>
            <h2 id={titleId} className="example-title">See the connection.</h2>
            <p id={descriptionId} className="hint">A sample lesson paired with a real Texas Grade 3 mathematics standard, retrieved from Learning Commons.</p>
            <div className="view-toggle" role="group" aria-label="Sample report view">
              <button className="toggle" type="button" aria-pressed={view === 'plan'} onClick={() => {
                if (view !== 'plan') { setView('plan'); track('example_view_changed', { view: 'plan' }); }
              }}>Your plan</button>
              <button className="toggle" type="button" aria-pressed={view === 'standards'} onClick={() => {
                if (view !== 'standards') { setView('standards'); track('example_view_changed', { view: 'standards' }); }
              }}>Standards matches</button>
            </div>
            <div className="example-content">
              {view === 'plan' ? (
                <>
                  <p className="eyebrow">From the sample lesson</p>
                  <h3>Comparing fractions</h3>
                  <p className="example-lesson">{EXAMPLE.lesson}</p>
                  <FractionIllustration />
                  <p><strong>Objective:</strong> Compare two fractions using fraction strips.</p>
                  <ExampleMatch onSourceClick={trackSource} />
                </>
              ) : (
                <>
                  <p className="eyebrow">Texas · Mathematics · Grade 3</p>
                  <ExampleMatch onSourceClick={trackSource} showEvidence />
                  <p className="hint">The shared numerator, fraction models, explanation, and comparison symbol connect the lesson to this standard. Review the evidence to decide how fully the lesson addresses it.</p>
                </>
              )}
            </div>
            <p className="hint example-source-note">This saved search returned a related match, not a guarantee of coverage.</p>
            <p className="attribution">{EXAMPLE.standard.attributionStatement} <a href={EXAMPLE.standard.license} target="_blank" rel="noopener noreferrer">CC BY 4.0</a> · Retrieved {EXAMPLE.retrievedAt.slice(0, 10)}.</p>
            <div className="example-footer">
              <p>Now check material you found.</p>
              <button type="button" className="primary" onClick={goToOwnPlan}>Check my material <span aria-hidden="true">→</span></button>
            </div>
          </section>}
        </div>
      </section>
    </>
  );
}
